/**
 * TellerPickerPopover — the composer's `@` pill for a group room: "Ask these twins", the
 * twins the next send asks.
 *
 * The pill is dimmed while nothing is picked and shows a count badge once twins are
 * picked; its tooltip names them. A click opens the room's twins as a checklist (a twin
 * that is not ready is disabled unless already picked) and a "Keep for next messages"
 * checkbox.
 *
 * Checking and unchecking are the page's job (`onToggle`): a check inserts the twin's
 * @mention without moving focus, so the list stays open; closing the popover hands focus
 * to the composer's input (`onRequestInputFocus`).
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AtSign } from '@digital-twins/design-system/icons'

import { Checkbox } from '@/components/ui/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

export interface TellerPickerOption {
  /** Persona token: the id the picks use. */
  personaId: string
  name: string
  /** Picked for the next send (takeable picks only, never a sent, pending one). */
  isChecked: boolean
  /** Not ready yet: cannot be picked (the server drops a not-ready pick). */
  isReady: boolean
}

interface TellerPickerPopoverProps {
  options: Array<TellerPickerOption>
  isKeepOn: boolean
  onToggle: (personaId: string) => void
  onKeepChange: (isOn: boolean) => void
  isDisabled?: boolean
  /** Focus the composer's input once the popover closes. */
  onRequestInputFocus?: () => void
  /** Prefix of every data-testid. */
  testIdPrefix?: string
}

export function TellerPickerPopover(props: TellerPickerPopoverProps) {
  const {
    options,
    isKeepOn,
    onToggle,
    onKeepChange,
    isDisabled = false,
    onRequestInputFocus,
    testIdPrefix = 'composer-speakers',
  } = props

  const [isOpen, setIsOpen] = useState(false)

  const { t } = useTranslation('app')

  const onOpenChange = (nextIsOpen: boolean) => {
    if (isDisabled && nextIsOpen) {
      return
    }
    setIsOpen(nextIsOpen)
  }

  const onCloseAutoFocus = (event: Event) => {
    if (!onRequestInputFocus) {
      return
    }
    event.preventDefault()
    onRequestInputFocus()
  }

  const picked = options.filter((option) => option.isChecked)
  const isAuto = picked.length === 0
  const title = t('roomPane.pickerTitle')
  const tooltip = isAuto
    ? t('roomPane.pickerTooltipNone')
    : t('roomPane.pickerTooltipPicked', { who: picked.map((option) => option.name).join(', ') })

  const badge = isAuto ? null : (
    <span
      className="min-w-[16px] rounded-full bg-primary px-1 text-center text-[10px] font-semibold leading-4 tabular-nums text-primary-foreground"
      data-testid={`${testIdPrefix}-badge`}
    >
      {picked.length}
    </span>
  )

  // asChild: one button is both the tooltip's and the popover's trigger.
  return (
    <Popover open={isOpen && !isDisabled} onOpenChange={onOpenChange}>
      <TooltipProvider delayDuration={400}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                type="button"
                disabled={isDisabled}
                aria-label={isAuto ? title : `${title}. ${tooltip}`}
                data-testid={testIdPrefix}
                data-auto={isAuto ? 'true' : 'false'}
                className={cn(
                  // Same box as the model select trigger beside it: 32px, input border, page background.
                  'inline-flex h-8 shrink-0 items-center gap-1 rounded-full border border-input bg-background px-2.5 text-xs font-medium transition-colors duration-150',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                  isAuto
                    ? 'text-[var(--home-stepper-fg)] opacity-60 hover:opacity-100'
                    : 'border-primary/50 text-foreground',
                  isDisabled && 'cursor-not-allowed opacity-40 hover:opacity-40',
                )}
              >
                <AtSign className="h-3.5 w-3.5" strokeWidth={2.2} aria-hidden="true" />
                {badge}
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="top" data-testid={`${testIdPrefix}-tooltip`}>
            {tooltip}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <PopoverContent
        side="top"
        align="end"
        className="w-64 p-1.5"
        onCloseAutoFocus={onCloseAutoFocus}
        data-testid={`${testIdPrefix}-popover`}
      >
        <p className="px-2 pb-1 pt-1 text-xs font-medium text-muted-foreground">{title}</p>
        <ul className="max-h-64 overflow-y-auto" aria-label={title}>
          {options.map((option, index) => {
            const isOptionDisabled = !option.isReady && !option.isChecked
            const checkboxId = `${testIdPrefix}-option-${index}-checkbox`
            return (
              <li key={option.personaId}>
                <label
                  htmlFor={checkboxId}
                  className={cn(
                    'flex min-h-9 items-center gap-2 rounded px-2 py-1.5 text-sm',
                    isOptionDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-accent',
                  )}
                  data-testid={`${testIdPrefix}-option-${index}`}
                >
                  <Checkbox
                    id={checkboxId}
                    checked={option.isChecked}
                    disabled={isOptionDisabled}
                    onCheckedChange={() => onToggle(option.personaId)}
                    data-testid={`${testIdPrefix}-option-${index}-checkbox`}
                  />
                  <span className="min-w-0 flex-1 truncate">{option.name}</span>
                  {!option.isReady ? (
                    <span className="shrink-0 text-xs text-muted-foreground">{t('roomPane.pickerNotReady')}</span>
                  ) : null}
                </label>
              </li>
            )
          })}
        </ul>
        <div className="mt-1 border-t border-border pt-1">
          <label
            htmlFor={`${testIdPrefix}-keep-checkbox`}
            className="flex min-h-9 cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent"
          >
            <Checkbox
              id={`${testIdPrefix}-keep-checkbox`}
              checked={isKeepOn}
              onCheckedChange={(checked) => onKeepChange(checked === true)}
              data-testid={`${testIdPrefix}-keep`}
            />
            <span>{t('roomPane.pickerKeep')}</span>
          </label>
        </div>
      </PopoverContent>
    </Popover>
  )
}
